import {
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import { type RefObject, useCallback, useEffect, useState } from 'react';

import { FeatureTrackingEvents } from '@revelio/core';
import { Button } from '@revelio/ui';

import { useBrand } from '../../../../../shared/branding/brand-provider';
import styles from '../../Modal/themedModal.module.css';
import localStyles from './DownloadPlotPng.module.css';

const HEADER_HEIGHT = 44;
const FOOTER_HEIGHT = 13;

type DownloadPlotPNGProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  printRef: RefObject<HTMLDivElement>;
  defaultFileName?: string;
};

export const DownloadPlotPNG = ({
  isOpen,
  onClose,
  title,
  printRef,
  defaultFileName,
}: DownloadPlotPNGProps) => {
  const brand = useBrand();
  const [transparentBackground, setTransparentBackground] = useState(false);
  const [fileName, setFileName] = useState('');
  const [plotTitle, setPlotTitle] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFileName(defaultFileName || title);
      setPlotTitle(title);
      setTransparentBackground(false);
    }
  }, [isOpen, title, defaultFileName]);

  const resetForm = useCallback(() => {
    setFileName(defaultFileName || title);
    setPlotTitle(title);
    setTransparentBackground(false);
  }, [title, defaultFileName]);

  const handleDownload = () => {
    const fileNameSnapshot = fileName;
    const plotTitleSnapshot = plotTitle;
    const transparentBackgroundSnapshot = transparentBackground;

    mixpanel.track(FeatureTrackingEvents.PNG_DOWNLOAD, {
      plot_title: title,
    });

    onClose();

    // Defer heavy processing to allow modal to close cleanly
    setTimeout(() => {
      (async () => {
        const html2canvas = (await import('html2canvas-pro')).default;

        const cardBody = printRef.current;
        const chartContainer = cardBody?.firstElementChild;
        if (!chartContainer) return;

        const { width, height } = chartContainer.getBoundingClientRect();
        const chartNode = chartContainer.cloneNode(true);

        const imageEl = document.createElement('div');
        imageEl.style.width = `${width + 20}px`;
        imageEl.style.height = `${height + HEADER_HEIGHT + FOOTER_HEIGHT + 4}px`;
        imageEl.style.padding = '0 10px';

        const headerEl = document.createElement('h1');
        headerEl.style.fontSize = width < 250 ? '1rem' : '1.5rem';
        headerEl.style.color = brand.sidebarColor[500];
        headerEl.style.fontWeight = '600';
        headerEl.style.marginBottom = '0.5rem';
        headerEl.style.textAlign = 'center';
        headerEl.style.width = '100%';
        headerEl.style.textOverflow = 'ellipsis';
        headerEl.style.whiteSpace = 'nowrap';
        headerEl.style.overflow = 'hidden';
        headerEl.innerHTML = plotTitleSnapshot;
        imageEl.appendChild(headerEl);

        const chartEl = document.createElement('div');
        chartEl.style.width = '100%';
        chartEl.style.height = `${height}px`;
        chartEl.style.margin = 'auto';
        chartEl.appendChild(chartNode);
        imageEl.appendChild(chartEl);

        const watermarkEl = document.createElement('img');
        watermarkEl.style.width = '85px';
        watermarkEl.style.height = `${FOOTER_HEIGHT}px`;
        watermarkEl.style.float = 'right';
        imageEl.appendChild(watermarkEl);

        // Convert the watermark SVG to a data URL so html2canvas can render it
        try {
          const resp = await fetch(brand.logos.watermark);
          const svgText = await resp.text();
          watermarkEl.src = `data:image/svg+xml;base64,${btoa(svgText)}`;
        } catch {
          watermarkEl.src = brand.logos.watermark;
        }

        document.body.append(imageEl);

        // Wait for the watermark image to load before capturing
        await new Promise<void>((resolve) => {
          if (watermarkEl.complete) {
            resolve();
          } else {
            watermarkEl.onload = () => resolve();
            watermarkEl.onerror = () => resolve();
          }
        });

        const canvas = await html2canvas(imageEl, {
          backgroundColor: transparentBackgroundSnapshot ? null : '#fff',
          onclone: (document: Document) => {
            Array.from(document?.querySelectorAll('*') || []).forEach((e) => {
              const existingStyle = e.getAttribute('style') || '';
              e.setAttribute(
                'style',
                existingStyle + '; font-size: 10 !important'
              );
            });
          },
        });

        const data = canvas.toDataURL();
        const link = document.createElement('a');

        if (typeof link.download === 'string') {
          link.href = data;
          link.download = `${fileNameSnapshot || 'plot'}.png`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          window.open(data);
        }

        imageEl.remove();
      })();
    }, 100);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent className={styles.content}>
        <ModalHeader className={styles.header}>
          {title} Plot Download
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired className={localStyles.formField}>
            <FormLabel fontSize="sm">File Name</FormLabel>
            <Input
              type="text"
              size="sm"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
            />
          </FormControl>
          <FormControl className={localStyles.formField}>
            <FormLabel fontSize="sm">Title</FormLabel>
            <Input
              type="text"
              size="sm"
              value={plotTitle}
              onChange={(e) => setPlotTitle(e.target.value)}
            />
          </FormControl>
          <FormControl className={localStyles.formField}>
            <Checkbox
              colorScheme="green"
              isChecked={transparentBackground}
              onChange={() =>
                setTransparentBackground((prevState) => !prevState)
              }
            >
              <Text fontSize="sm">Transparent Background</Text>
            </Checkbox>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Flex width="100%" justifyContent="space-between">
            <Button
              size="sm"
              variant="ghost"
              color="destructive"
              onClick={resetForm}
            >
              Reset
            </Button>
            <div>
              <Button
                size="sm"
                variant="link"
                className={localStyles.cancelBtn}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button color="primary" size="sm" onClick={handleDownload}>
                Download
              </Button>
            </div>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
